/**
 * Typed translation hooks.
 *
 * Import these rather than reaching for `react-i18next` directly — they pin the
 * namespace union to the five catalogues this app ships, so autocomplete lists
 * the real keys and a typo fails the build.
 *
 *   const { t } = useT("tickets");
 *   t("status.WAITING_CUSTOMER");        // checked
 *   t("status.WAITNG_CUSTOMER");         // compile error
 *
 *   const { language, locale, changeLanguage } = useLanguage();
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ensureLanguageLoaded } from "@/lib/i18n";

import {
  type AppLanguage,
  type AppLanguageDescriptor,
  getLanguageDescriptor,
  LANGUAGE_STORAGE_KEY,
  OFFERED_LANGUAGES,
  resolveAppLanguage,
} from "./languages";
import { type AppNamespace, DEFAULT_NAMESPACE } from "./resources";

/** Remember an explicit pick. Storage may be blocked; the switch still applies. */
function persistLanguageChoice(next: AppLanguage): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
  } catch {
    // Private mode / blocked storage: the choice lasts for this page only.
  }
}

/**
 * `useTranslation` narrowed to this app's namespaces.
 *
 * Defaults to `common`, so `useT()` covers the shell and shared primitives.
 */
export function useT<N extends AppNamespace = typeof DEFAULT_NAMESPACE>(namespace?: N) {
  return useTranslation((namespace ?? DEFAULT_NAMESPACE) as N);
}

export interface UseLanguageResult {
  /** The active language key, always one this app ships. */
  language: AppLanguage;
  /** Full descriptor: endonym, English name, flag code, locale. */
  descriptor: AppLanguageDescriptor;
  /** BCP-47 locale for `Intl.*` and the design system `I18nProvider`. */
  locale: string;
  /** Every language the switcher offers, in display order. */
  languages: typeof OFFERED_LANGUAGES;
  /** Switch language and persist the choice to localStorage. */
  changeLanguage: (next: AppLanguage) => void;
}

/**
 * Read and change the active language.
 *
 * `changeLanguage` is the explicit pick and is persisted to localStorage here,
 * so it survives a reload and outranks `navigator.language` from then on. The
 * detector itself caches nothing (`lib/i18n.ts`), so a language that was only
 * auto-detected never sticks to a shared PC.
 */
export function useLanguage(): UseLanguageResult {
  const { i18n } = useTranslation();
  const language = resolveAppLanguage(i18n.resolvedLanguage ?? i18n.language);
  const descriptor = getLanguageDescriptor(language);

  const changeLanguage = useCallback(
    (next: AppLanguage) => {
      /* Load the catalogue *then* switch. Switching first would show English
         for as long as the chunk takes, which on the connections this split
         was made for is long enough to read. */
      persistLanguageChoice(next);
      void ensureLanguageLoaded(next).then(() => i18n.changeLanguage(next));
    },
    [i18n],
  );

  return {
    language,
    descriptor,
    locale: descriptor.locale,
    languages: OFFERED_LANGUAGES,
    changeLanguage,
  };
}
